import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiCalendar, FiLock, FiGlobe, FiFile, FiClock, FiRefreshCw,
  FiList, FiCheckCircle, FiAlertCircle,
} = FiIcons;

const LABELS = {
  payment_schedule: {
    on_release: 'On Release', weekly: 'Weekly', monthly: 'Monthly',
    quarterly: 'Quarterly', milestone: 'Per Milestone',
  },
  exclusivity: {
    exclusive: 'Exclusive', non_exclusive: 'Non-Exclusive', genre_locked: 'Genre-Locked',
  },
  territory: {
    worldwide: 'Worldwide', north_america: 'North America', europe: 'Europe',
    asia_pacific: 'Asia Pacific', latin_america: 'Latin America', custom: 'Custom',
  },
  content_rights: {
    shared: 'Shared Rights', initiator_owns: 'Initiator Owns',
    partner_owns: 'Partner Owns', licensed: 'Licensed Use',
  },
};

const Row = ({ icon, label, value, accent }) => (
  <div className="flex items-center justify-between py-3 border-b border-zinc-800/60 last:border-0">
    <div className="flex items-center gap-2.5">
      <SafeIcon icon={icon} className="text-zinc-600 text-sm flex-shrink-0" />
      <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</span>
    </div>
    <span className={`text-xs font-black uppercase tracking-widest ${accent || 'text-white'}`}>{value}</span>
  </div>
);

const TermsViewer = ({ terms, revSplit, isInitiator, agreedAt, version }) => {
  if (!terms || Object.keys(terms).length === 0) return (
    <div className="flex items-center gap-2 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
      <SafeIcon icon={FiAlertCircle} className="text-amber-400 text-sm" />
      <p className="text-xs font-black text-amber-400 uppercase tracking-widest">No terms defined yet</p>
    </div>
  );

  const myRevSplit = isInitiator ? revSplit : 100 - revSplit;

  return (
    <div className="space-y-4">
      {/* Agreement badge */}
      {agreedAt ? (
        <div className="flex items-center gap-2 p-3 bg-green-500/8 border border-green-500/20 rounded-xl">
          <SafeIcon icon={FiCheckCircle} className="text-green-400 text-sm" />
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-green-400">Terms Agreed</p>
            <p className="text-[10px] font-bold text-zinc-500">{new Date(agreedAt).toLocaleDateString()}</p>
          </div>
          {version && (
            <span className="ml-auto text-[10px] font-black text-zinc-600 uppercase tracking-widest">v{version}</span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl">
          <SafeIcon icon={FiAlertCircle} className="text-amber-400 text-sm" />
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-400">Awaiting Agreement</p>
          {version && (
            <span className="ml-auto text-[10px] font-black text-zinc-600 uppercase tracking-widest">v{version}</span>
          )}
        </div>
      )}

      {/* Terms rows */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl px-5 py-1">
        {revSplit !== undefined && (
          <Row icon={FiFile} label="Your Revenue Cut" value={`${myRevSplit}%`} accent="text-green-400" />
        )}
        {terms.payment_schedule && (
          <Row icon={FiCalendar} label="Payment Schedule" value={LABELS.payment_schedule[terms.payment_schedule] || terms.payment_schedule} />
        )}
        {terms.exclusivity && (
          <Row icon={FiLock} label="Exclusivity"
            value={LABELS.exclusivity[terms.exclusivity] || terms.exclusivity}
            accent={terms.exclusivity === 'exclusive' ? 'text-amber-400' : 'text-white'} />
        )}
        {terms.territory && (
          <Row icon={FiGlobe} label="Territory" value={LABELS.territory[terms.territory] || terms.territory} />
        )}
        {terms.content_rights && (
          <Row icon={FiFile} label="Content Rights" value={LABELS.content_rights[terms.content_rights] || terms.content_rights} />
        )}
        {terms.cancellation_notice_days !== undefined && (
          <Row icon={FiClock} label="Cancellation Notice" value={`${terms.cancellation_notice_days} days`} />
        )}
        {terms.auto_renew !== undefined && (
          <Row icon={FiRefreshCw} label="Auto-Renew"
            value={terms.auto_renew ? 'Yes' : 'No'}
            accent={terms.auto_renew ? 'text-green-400' : 'text-zinc-400'} />
        )}
      </div>

      {/* Custom clauses */}
      {(terms.custom_clauses || []).length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiList} className="text-zinc-600 text-sm" />
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Custom Clauses</p>
          </div>
          {terms.custom_clauses.map((clause, i) => (
            <div key={i} className="flex items-start gap-2.5 p-3 bg-zinc-900/40 border border-zinc-800/60 rounded-xl">
              <span className="text-[10px] font-black text-zinc-600 mt-0.5 flex-shrink-0">{i + 1}.</span>
              <p className="text-xs font-bold text-zinc-300 leading-relaxed">{clause}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TermsViewer;